// ------------------------------------------------------------
// Application Entry Point
// ------------------------------------------------------------
// This file sets up the root application component with React Router.
// It loads routes from the centralized configuration (routes.js) and
// renders them recursively, including nested child routes.
//
// Features included:
// - Centralized routing via routes.js
// - Recursive render function for nested routes
// - React Router v6 with v7 future flags enabled
// ------------------------------------------------------------

import React from "react";
import { BrowserRouter as Router, Routes, Route } from "react-router-dom";
import { routes } from "./routes.jsx";
import { Toaster } from "react-hot-toast";
import { TooltipProvider } from "./components/ui/tooltip.jsx";

/**
 * renderRoutes
 * ------------------------------------------------------------
 * Recursively renders <Route> elements for nested route configs.
 *
 * @param {Array<Object>} list - Array of route configuration objects.
 * @returns {JSX.Element[]} - Array of <Route> components.
 */
const renderRoutes = (list) =>
  list.map(({ path, element, children }, index) => (
    <Route key={`${path}-${index}`} path={path} element={element}>
      {children && renderRoutes(children)}
    </Route>
  ));

/**
 * App
 * ------------------------------------------------------------
 * Root application component.
 * - Wraps the app with React Router.
 * - Renders all routes from the centralized config.
 * - Enables experimental v7 React Router features for future support.
 *
 * @returns {JSX.Element} - The application root.
 */
export default function App() {
  return (
    <TooltipProvider delayDuration={0}>
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <Routes>{renderRoutes(routes)}</Routes>
      <Toaster position="top-center" reverseOrder={false} />
    </Router>
    </TooltipProvider>
  );
}
